// =============================================================================
// MeuDriver — Batch U3: Editor de tags de especialidade do instrutor
// Usado dentro da aba "Especialidades" em InstructorEditProfile
// =============================================================================

import { useState, useEffect } from 'react'
import { Tag } from 'lucide-react'
import { useAllTagDefinitions, useInstructorTags, useUpdateInstructorTags } from '@/hooks/useInstructorTags'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { InstructorTagDefinitionRow } from '@/types/database'

const MAX_TAGS = 8

interface InstructorTagsEditorProps {
  instructorId: string
}

export function InstructorTagsEditor({ instructorId }: InstructorTagsEditorProps) {
  const { data: definitions = [], isLoading: loadingDefs } = useAllTagDefinitions()
  const { data: currentTagIds = [], isLoading: loadingCurrent } = useInstructorTags(instructorId)
  const updateTags = useUpdateInstructorTags(instructorId)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dirty, setDirty] = useState(false)

  // Inicializa seleção com tags atuais
  useEffect(() => {
    if (!loadingCurrent) {
      setSelected(new Set(currentTagIds))
      setDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCurrent, currentTagIds.join(',')])

  // Agrupa por categoria
  const byCategory: Record<string, InstructorTagDefinitionRow[]> = {}
  for (const def of definitions) {
    const cat = def.category ?? 'Outros'
    if (!byCategory[cat]) byCategory[cat] = []
    byCategory[cat].push(def)
  }

  function toggle(tagId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        if (next.size >= MAX_TAGS) return prev // limite
        next.add(tagId)
      }
      return next
    })
    setDirty(true)
  }

  function handleSave() {
    updateTags.mutate(Array.from(selected), {
      onSuccess: () => setDirty(false),
    })
  }

  const isLoading = loadingDefs || loadingCurrent

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-brand-600" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Minhas especialidades
        </h3>
        <span className="text-xs text-muted-foreground ml-auto">
          {selected.size}/{MAX_TAGS} selecionadas
        </span>
      </div>

      <p className="text-sm text-muted-foreground">
        Escolha até {MAX_TAGS} tags que descrevem seus diferenciais. Elas aparecem no seu perfil público.
      </p>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(byCategory).map(([category, tags]) => (
            <div key={category}>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {category}
              </p>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const active = selected.has(tag.id)
                  const atLimit = !active && selected.size >= MAX_TAGS
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      data-testid={`tag-${tag.id}`}
                      disabled={atLimit}
                      onClick={() => toggle(tag.id)}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                        active
                          ? 'bg-brand-600 text-white border-brand-600'
                          : atLimit
                          ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-brand-400 hover:text-brand-600 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600',
                      )}
                    >
                      {tag.label}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {dirty && (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={updateTags.isPending}
            className="gap-1.5"
          >
            {updateTags.isPending ? 'Salvando...' : 'Salvar especialidades'}
          </Button>
        </div>
      )}
    </div>
  )
}
